import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Box,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

// Map search types to human-readable labels
const SEARCH_TYPE_LABELS = {
  parliamentarians: 'Parliamentarians',
  hansard: 'Hansard',
  bills: 'Bills',
  committees: 'Committees',
  qon: 'Questions on Notice',
  qiw: 'Questions in Writing',
  tableddocs: 'Tabled Documents',
  electorates: 'Electorates',
  parties: 'Parties',
  ministries: 'Ministries',
  divisions: 'Divisions',
  senate_divisions: 'Senate Divisions',
  motions: 'Motions',
  petitions: 'Petitions',
  committee_reports: 'Committee Reports',
  global: 'Global Search',
};

// Map search types to colors
const SEARCH_TYPE_COLORS = {
  parliamentarians: '#1976d2',
  hansard: '#9c27b0',
  bills: '#2e7d32',
  committees: '#ed6c02',
  qon: '#0288d1',
  qiw: '#0288d1',
  tableddocs: '#7b1fa2',
  electorates: '#d32f2f',
  parties: '#f57c00',
  ministries: '#0097a7',
  divisions: '#5d4037',
  senate_divisions: '#5d4037',
  motions: '#616161',
  petitions: '#00796b',
  committee_reports: '#303f9f',
  global: '#424242',
};

function SavedSearchesPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editSearch, setEditSearch] = useState(null);
  const [editName, setEditName] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Delete dialog state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteSearch, setDeleteSearch] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Filter state
  const [sortBy, setSortBy] = useState('last_run_at');
  const [filterType, setFilterType] = useState('');

  const fetchSearches = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('sort_by', sortBy);
      if (filterType) {
        params.append('search_type', filterType);
      }

      const response = await api.get(`/saved-searches?${params.toString()}`);
      setSearches(response.data.searches || []);
      setError('');
    } catch (err) {
      console.error('Error fetching saved searches:', err);
      if (err.response?.status === 401) {
        setError('Please log in to view your saved searches');
      } else {
        setError('Failed to load saved searches');
      }
    } finally {
      setLoading(false);
    }
  }, [sortBy, filterType]);

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchSearches();
    } else if (!authLoading && !isAuthenticated) {
      setLoading(false);
    }
  }, [isAuthenticated, authLoading, fetchSearches]);

  const handleRun = async (search) => {
    try {
      const response = await api.post(`/saved-searches/${search.id}/run`);
      const redirectUrl = response.data.redirect_url;

      // Navigate to the search URL
      navigate(redirectUrl);
    } catch (err) {
      console.error('Error running search:', err);
      setError('Failed to run saved search');
    }
  };

  const handleEditOpen = (search) => {
    setEditSearch(search);
    setEditName(search.name);
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setEditSearch(null);
    setEditName('');
  };

  const handleEditSave = async () => {
    if (!editName.trim()) return;

    setEditSaving(true);
    try {
      await api.put(`/saved-searches/${editSearch.id}`, {
        name: editName.trim(),
      });
      handleEditClose();
      fetchSearches();
    } catch (err) {
      console.error('Error updating search:', err);
      setError('Failed to update saved search');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeleteOpen = (search) => {
    setDeleteSearch(search);
    setDeleteOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
    setDeleteSearch(null);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/saved-searches/${deleteSearch.id}`);
      handleDeleteClose();
      fetchSearches();
    } catch (err) {
      console.error('Error deleting search:', err);
      setError('Failed to delete saved search');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Show login message if not authenticated
  if (!authLoading && !isAuthenticated) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <BookmarkIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Saved Searches
          </Typography>
          <Typography color="text.secondary" paragraph>
            Log in to save and manage your searches.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/login')}
          >
            Log In
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Saved Searches
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Filter by Type</InputLabel>
            <Select
              value={filterType}
              label="Filter by Type"
              onChange={(e) => setFilterType(e.target.value)}
            >
              <MenuItem value="">All Types</MenuItem>
              {Object.entries(SEARCH_TYPE_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortBy}
              label="Sort By"
              onChange={(e) => setSortBy(e.target.value)}
            >
              <MenuItem value="last_run_at">Recently Used</MenuItem>
              <MenuItem value="created_at">Date Created</MenuItem>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="run_count">Most Used</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : searches.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <BookmarkIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No saved searches yet
          </Typography>
          <Typography color="text.secondary">
            When you find a search you want to save, click the "Save Search" button.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Query</TableCell>
                <TableCell align="right">Results</TableCell>
                <TableCell>Last Run</TableCell>
                <TableCell align="right">Times Run</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {searches.map((search) => (
                <TableRow key={search.id} hover>
                  <TableCell>
                    <Typography fontWeight="medium">
                      {search.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={SEARCH_TYPE_LABELS[search.search_type] || search.search_type}
                      size="small"
                      sx={{
                        backgroundColor: SEARCH_TYPE_COLORS[search.search_type] || '#666',
                        color: 'white',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {search.query_text === '*' ? 'All' : search.query_text}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {search.result_count?.toLocaleString() || '-'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(search.last_run_at)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {search.run_count}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Run Search">
                      <IconButton
                        color="primary"
                        onClick={() => handleRun(search)}
                        size="small"
                      >
                        <PlayArrowIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        onClick={() => handleEditOpen(search)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteOpen(search)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Saved Search</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Search Name"
            fullWidth
            variant="outlined"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            disabled={editSaving}
            inputProps={{ maxLength: 100 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose} disabled={editSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleEditSave}
            variant="contained"
            disabled={editSaving || !editName.trim()}
          >
            {editSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onClose={handleDeleteClose}>
        <DialogTitle>Delete Saved Search</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteSearch?.name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default SavedSearchesPage;
